import type React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {Alert, Box, InputAdornment, Stack, TextField} from '@mui/material';
import {Search} from '@mui/icons-material';
import type {GridColDef, GridPaginationModel, GridSortModel} from '@mui/x-data-grid-premium';
import {useOutletContext} from 'react-router';
import {CustomDataGrid} from '~/components/data-grid/CustomDataGrid';
import {formatDate} from '~/components/data-grid/cells/gridCellFormat';
import {useProfileTransactionsQuery} from '../../hooks/useCategoryProfileQueries';
import type {TransactionRow} from '../../types/categoryProfileTypes';
import {toOrdering} from '../../domain/profileTables';
import {ProfileSection} from '../components/ProfileSection';
import type {CategoryProfileOutletContext} from '../profileTypes';

/** Transactions tab (spec 4.8): the category's lines, searched, paged and sorted on the server (the grid's own filter
 * would only see the page on screen). */
export const TransactionsTab: React.FC = () => {
    const {databagId, path, header, money} = useOutletContext<CategoryProfileOutletContext>();
    const [pagination, setPagination] = useState<GridPaginationModel>({page: 0, pageSize: 10});
    const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'amount', sort: 'desc'}]);
    const [searchText, setSearchText] = useState('');
    const [search, setSearch] = useState('');

    // Search as you type, without a request per key press.
    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchText.trim());
            setPagination(p => ({...p, page: 0}));
        }, 300);
        return () => clearTimeout(t);
    }, [searchText]);

    const {data, isFetching, isError} = useProfileTransactionsQuery(databagId, {
        path, ordering: toOrdering(sortModel), page: pagination.page, pageSize: pagination.pageSize, search,
    });

    const columns: GridColDef<TransactionRow>[] = useMemo(() => [
        {field: 'materialId', headerName: 'Material ID', width: 140, sortable: false},
        {field: 'description', headerName: 'Description', flex: 1, minWidth: 240, sortable: false,
            valueFormatter: (v: string) => v || '—'},
        {field: 'vendorName', headerName: 'Vendor', width: 200, sortable: false},
        {field: 'site', headerName: 'Site', width: 140, sortable: false},
        {field: 'date', headerName: 'Date', width: 120, valueFormatter: (v: string | null) => formatDate(v)},
        {field: 'quantity', headerName: 'Quantity', type: 'number', width: 110, sortable: false},
        {field: 'amount', headerName: 'Amount', type: 'number', width: 130, valueFormatter: (v: number) => money(v)},
        {field: 'avgUnitCost', headerName: 'Avg. unit cost', type: 'number', width: 130, sortable: false,
            valueFormatter: (v: number | null) => v === null ? '—' : money(v)},
    ], [money]);

    if (isError) return <Alert severity="error">Could not load the transactions.</Alert>;
    return (
        <ProfileSection title={`Transactions in ${header.name}`} subtitle="Last 12 months with data">
            <Stack gap={1.5}>
                <TextField size="small" placeholder="Search description, material or vendor" value={searchText}
                           onChange={e => setSearchText(e.target.value)} sx={{maxWidth: 360}}
                           slotProps={{htmlInput: {maxLength: 100, 'aria-label': 'Search transactions'},
                               input: {startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>}}}/>
                <Box sx={{height: 560}}>
                    <CustomDataGrid
                        layoutKey="category-profile-transactions"
                        rows={data?.rows ?? []} columns={columns} loading={isFetching} getRowId={r => r.partId}
                        rowCount={data?.count ?? 0}
                        paginationMode="server" sortingMode="server"
                        paginationModel={pagination} onPaginationModelChange={setPagination}
                        sortModel={sortModel} onSortModelChange={m => {
                            setSortModel(m);
                            setPagination(p => ({...p, page: 0}));
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        disableRowSelectionOnClick showToolbar
                        toolbarProps={{title: `${(data?.count ?? 0).toLocaleString()} transactions`, showFilterPanel: false}}
                        localeText={{noRowsLabel: 'No transactions match.'}}
                    />
                </Box>
            </Stack>
        </ProfileSection>
    );
};
